import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Crown, Target, Loader, Plus, Search, Sparkles, RefreshCw,
  MapPin, Award, ChevronRight, Send, ArrowRight, X, ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const NAVY = "#0A2540";
const GREEN = "#047857";
const GREEN_DEEP = "#064E3B";
const GOLD = "#D4AF37";
const CREAM = "#FBF7EE";

const TIER_PALETTE = {
  gold:   { bg: "#FEF3C7", color: "#78350F", border: GOLD, label: "Gold" },
  silver: { bg: "#E0F2FE", color: "#1E3A8A", border: "#1E40AF", label: "Silver" },
  bronze: { bg: "#FFE4D2", color: "#7C2D12", border: "#C2410C", label: "Bronze" },
};

export default function RecruiterMatchmaker() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requisitions, setRequisitions] = useState([]);
  const [active, setActive] = useState(null);   // selected requisition (with matches)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // Guard: must be a recruiter
  useEffect(() => {
    if (user === null) return;
    if (user === false || !user?.is_recruiter) {
      navigate("/recruiter/signup");
    }
  }, [user, navigate]);

  const loadList = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/recruiter/requisitions");
      setRequisitions(data.requisitions || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load requisitions.");
    } finally {
      setLoading(false);
    }
  };

  const loadOne = async (id) => {
    try {
      const { data } = await api.get(`/recruiter/requisitions/${id}`);
      setActive(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load matches.");
    }
  };

  useEffect(() => {
    if (user?.is_recruiter) loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleCreated = async (req) => {
    setShowCreate(false);
    setActive(req);
    setRequisitions((prev) => [req, ...prev]);
  };

  if (!user || !user.is_recruiter) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
          <div className="rm-card-static p-6 flex items-center gap-2"
               style={{ background: "#F8FAFC", borderColor: "#CBD5E1" }}>
            <Loader size={16} className="animate-spin" style={{ color: NAVY }} />
            <span className="font-mono-rm text-[11px] uppercase tracking-widest">Redirecting…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen rm-paper" data-testid="matchmaker-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
        <Hero onCreate={() => setShowCreate(true)} />

        {error && (
          <div className="rm-card-static p-3 mt-4 text-xs font-mono-rm"
               style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
               data-testid="matchmaker-error">
            · {error}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5 md:gap-6 mt-6">
          {/* LEFT — Requisition list */}
          <div className="md:col-span-1">
            <div className="rm-card-static p-4"
                 style={{ background: "white", borderColor: NAVY, boxShadow: `4px 4px 0 0 ${GREEN}` }}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                     style={{ color: GREEN_DEEP, fontWeight: 700 }}>
                  · Your requisitions · {requisitions.length}
                </div>
                <button onClick={loadList}
                        className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                        data-testid="matchmaker-refresh-list-btn">
                  <RefreshCw size={11} /> Refresh
                </button>
              </div>
              {loading ? (
                <div className="text-xs font-mono-rm" style={{ color: "#475569" }}>· Loading…</div>
              ) : requisitions.length === 0 ? (
                <div className="text-center py-8" data-testid="matchmaker-list-empty">
                  <Target size={32} style={{ color: "#CBD5E1", margin: "0 auto" }} />
                  <p className="text-sm mt-2" style={{ color: "#475569" }}>
                    No requisitions yet. Create your first one to see ranked candidates.
                  </p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {requisitions.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => loadOne(r.id)}
                        className="rm-card-static p-3 w-full text-left hover:shadow-md transition"
                        style={{
                          background: active?.id === r.id ? "#ECFDF5" : "#F8FAFC",
                          borderColor: active?.id === r.id ? GREEN : "#E2E8F0",
                        }}
                        data-testid={`matchmaker-req-list-${r.id}`}
                      >
                        <div className="font-display uppercase text-sm leading-tight"
                             style={{ color: NAVY, letterSpacing: "-0.01em" }}>
                          {r.role}
                        </div>
                        {(r.company || r.location) && (
                          <div className="text-[11px] mt-0.5" style={{ color: "#475569" }}>
                            {r.company || "—"}{r.location ? ` · ${r.location}` : ""}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="font-mono-rm text-[9px] uppercase tracking-widest"
                                style={{ color: GREEN, fontWeight: 700 }}>
                            · {r.match_count ?? 0} matched
                          </span>
                          {r.last_matched_at && (
                            <span className="font-mono-rm text-[9px] uppercase tracking-widest"
                                  style={{ color: "#94A3B8" }}>
                              · {timeAgo(r.last_matched_at)}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* RIGHT — Matches detail */}
          <div className="md:col-span-2">
            {active ? (
              <MatchesPanel
                requisition={active}
                onRefreshed={(matches, last_matched_at) => {
                  const next = { ...active, matches, last_matched_at, match_count: matches.length };
                  setActive(next);
                  setRequisitions((prev) => prev.map((r) => (r.id === active.id ? { ...r, last_matched_at, match_count: matches.length } : r)));
                }}
                onError={setError}
              />
            ) : (
              <EmptyMatches onCreate={() => setShowCreate(true)} />
            )}
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateRequisitionModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}

/* ─────────────────────  Subcomponents  ───────────────────── */

function Hero({ onCreate }) {
  return (
    <div className="rm-card-static p-5 md:p-7"
         style={{
           background: `linear-gradient(135deg, ${NAVY} 0%, #0F1B2D 50%, ${GREEN_DEEP} 100%)`,
           borderColor: NAVY,
           boxShadow: `6px 6px 0 0 ${GOLD}`,
         }}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
               style={{ color: GOLD, fontWeight: 700 }}>
            · Resmo Pro · Talent Cloud Matchmaker
          </div>
          <h1 className="font-display uppercase leading-none mb-2"
              style={{ fontSize: "clamp(1.5rem, 3.4vw, 2.4rem)", color: "white", letterSpacing: "-0.02em" }}
              data-testid="matchmaker-title">
            Find <span style={{ color: GOLD }}>verified</span> candidates,{" "}
            <span style={{ color: "#A7F3D0" }}>permission-first.</span>
          </h1>
          <p className="text-sm md:text-[15px] max-w-2xl leading-snug" style={{ color: "#CBD5E1" }}>
            Post a requisition — Resmo ranks every opted-in candidate by skill
            overlap, tier, and recency. Recruiters get curated matches.
            Candidates control whether you ever reach out. Double opt-in by
            design.
          </p>
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <span className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                  style={{ color: "#FBBF24", fontWeight: 700 }}>
              <ShieldCheck size={12} /> 50 credits per requisition
            </span>
            <span className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                  style={{ color: "#A7F3D0", fontWeight: 700 }}>
              <CheckCircle2 size={12} /> Verified Honesty Seal on every match
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <img
            src="/resmo-pro-mascot.png"
            alt="Resmo Pro · Talent Cloud mascot"
            className="rounded-md"
            style={{ width: 140, height: 140, border: `3px solid ${GOLD}`, background: CREAM }}
            data-testid="matchmaker-mascot"
          />
          <button
            type="button"
            onClick={onCreate}
            className="rm-btn text-sm px-4 py-2.5"
            style={{
              background: GREEN, color: "white",
              border: `2px solid ${GREEN_DEEP}`,
              boxShadow: `4px 4px 0 0 ${GOLD}`, fontWeight: 700,
            }}
            data-testid="matchmaker-open-create-btn"
          >
            <Plus size={14} /> New requisition
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyMatches({ onCreate }) {
  return (
    <div className="rm-card-static p-7 md:p-9 text-center"
         style={{ background: "white", borderColor: NAVY, boxShadow: `4px 4px 0 0 ${NAVY}` }}
         data-testid="matchmaker-empty-detail">
      <Search size={36} style={{ color: NAVY, margin: "0 auto" }} />
      <h2 className="font-display uppercase text-xl mt-3"
          style={{ color: NAVY, letterSpacing: "-0.02em" }}>
        Pick a requisition — or create one.
      </h2>
      <p className="text-sm mt-1.5 max-w-md mx-auto" style={{ color: "#475569" }}>
        Each requisition runs against the Talent Cloud (opted-in candidates
        only) and returns up to 10 ranked matches. The score blends tier,
        skill overlap, role relevance, and recency.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="rm-btn mt-4 text-sm px-4 py-2.5"
        style={{
          background: NAVY, color: "white",
          border: `2px solid ${GREEN_DEEP}`,
          boxShadow: `4px 4px 0 0 ${GREEN}`, fontWeight: 700,
        }}
        data-testid="matchmaker-empty-create-btn"
      >
        <Plus size={14} /> Create requisition
      </button>
    </div>
  );
}

function MatchesPanel({ requisition, onRefreshed, onError }) {
  const [busy, setBusy] = useState(false);
  const matches = requisition.matches || [];

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/recruiter/requisitions/${requisition.id}/refresh`);
      onRefreshed?.(data.matches || [], data.last_matched_at);
    } catch (e) {
      onError?.(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't refresh matches.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rm-card-static p-5"
         style={{ background: "white", borderColor: NAVY, boxShadow: `4px 4px 0 0 ${NAVY}` }}
         data-testid="matchmaker-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="font-mono-rm uppercase text-[10px] tracking-widest"
               style={{ color: GREEN, fontWeight: 700 }}>
            · Requisition · {requisition.id.slice(0, 8)}
          </div>
          <h2 className="font-display uppercase text-xl md:text-2xl leading-tight"
              style={{ color: NAVY, letterSpacing: "-0.02em" }}
              data-testid="matchmaker-detail-title">
            {requisition.role}
          </h2>
          <div className="text-[12px] mt-0.5" style={{ color: "#475569" }}>
            {requisition.company ? `${requisition.company} · ` : ""}
            {requisition.location || "Any location"}
            {requisition.seniority ? ` · ${requisition.seniority}` : ""}
            {requisition.salary_band ? ` · ${requisition.salary_band}` : ""}
          </div>
          {(requisition.required_skills?.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {requisition.required_skills.map((s) => (
                <span key={s}
                      className="font-mono-rm uppercase text-[9px] tracking-widest px-2 py-0.5 rounded"
                      style={{ background: "#ECFDF5", color: GREEN_DEEP, border: `1px solid ${GREEN}`, fontWeight: 700 }}>
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
        <button onClick={refresh}
                disabled={busy}
                className="rm-btn text-xs px-3 py-2"
                style={{
                  background: "white", color: NAVY,
                  border: `2px solid ${NAVY}`, fontWeight: 700,
                }}
                data-testid="matchmaker-refresh-btn">
          {busy ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {busy ? "Re-matching…" : "Re-run match (50 cr)"}
        </button>
      </div>

      <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-2"
           style={{ color: NAVY, fontWeight: 700 }}>
        · {matches.length} matches · ranked by fit
      </div>

      {matches.length === 0 ? (
        <div className="text-center py-6 text-sm" style={{ color: "#475569" }}>
          No matches in the Talent Cloud yet for these criteria. As candidates
          opt in, refresh to see them appear.
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="matchmaker-matches">
          {matches.map((m, i) => (
            <MatchRow key={m.user_id} match={m} rank={i + 1} reqId={requisition.id} onError={onError} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MatchRow({ match, rank, reqId, onError }) {
  const tierP = TIER_PALETTE[match.tier] || TIER_PALETTE.bronze;
  const [intro, setIntro] = useState(match.intro || null);
  const [busy, setBusy] = useState(false);

  const requestIntro = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/recruiter/requisitions/${reqId}/intros/${match.user_id}`);
      setIntro(data.intro || { status: "pending" });
    } catch (e) {
      onError?.(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send intro request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className="rm-card-static p-3"
      style={{ background: "#F8FAFC", borderColor: "#E2E8F0" }}
      data-testid={`matchmaker-match-${match.user_id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div
          className="flex-shrink-0 flex items-center justify-center font-display"
          style={{
            width: 52, height: 52, borderRadius: 8,
            background: NAVY, color: GOLD,
            border: `2px solid ${GOLD}`, fontSize: "1.3rem",
          }}
          title={`Rank #${rank}`}
        >
          {match.display_score}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display uppercase text-base leading-tight"
                  style={{ color: NAVY, letterSpacing: "-0.01em" }}>
              {match.first_name} · #{rank}
            </span>
            <span
              className="font-mono-rm text-[9.5px] uppercase tracking-widest px-2 py-0.5 rounded inline-flex items-center gap-1"
              style={{ background: tierP.bg, color: tierP.color, border: `1px solid ${tierP.border}`, fontWeight: 700 }}
              data-testid={`match-tier-${match.user_id}`}
            >
              <Crown size={9} /> {tierP.label}
            </span>
            {typeof match.fit_score === "number" && (
              <span className="font-mono-rm text-[9.5px] uppercase tracking-widest px-2 py-0.5 rounded"
                    style={{ background: "#ECFDF5", color: GREEN_DEEP, border: `1px solid ${GREEN}`, fontWeight: 700 }}>
                Fit {match.fit_score}/100
              </span>
            )}
          </div>
          <div className="text-[12px] mt-0.5" style={{ color: "#475569" }}>
            {match.last_role ? <span>{match.last_role}</span> : <span style={{ color: "#94A3B8" }}>No recent role</span>}
            {match.last_company ? <span> · {match.last_company}</span> : null}
            {match.location ? (
              <span className="ml-1 inline-flex items-center gap-1">· <MapPin size={10} /> {match.location}</span>
            ) : null}
          </div>
          {(match.matched_required?.length > 0 || match.top_skills?.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {(match.matched_required || []).slice(0, 6).map((s) => (
                <span key={`req-${s}`}
                      className="font-mono-rm text-[9px] uppercase tracking-widest px-2 py-0.5 rounded"
                      style={{ background: "#ECFDF5", color: GREEN_DEEP, border: `1px solid ${GREEN}`, fontWeight: 700 }}>
                  <CheckCircle2 size={9} style={{ display: "inline", marginRight: 3, verticalAlign: -1 }} />{s}
                </span>
              ))}
              {(match.top_skills || []).filter(s => !(match.matched_required || []).includes(s.toLowerCase())).slice(0, 4).map((s) => (
                <span key={`top-${s}`}
                      className="font-mono-rm text-[9px] uppercase tracking-widest px-2 py-0.5 rounded"
                      style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #E2E8F0" }}>
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {intro?.status === "accepted" && intro?.candidate?.email ? (
            <a href={`mailto:${intro.candidate.email}?subject=Resmo%20Pro%20intro`}
               className="rm-btn text-[11px] px-3 py-2"
               style={{ background: GREEN, color: "white", border: `2px solid ${GREEN_DEEP}`, fontWeight: 700 }}
               data-testid={`match-email-${match.user_id}`}>
              <Send size={11} /> Email candidate
            </a>
          ) : intro?.status === "pending" ? (
            <span className="font-mono-rm text-[10px] uppercase tracking-widest px-2 py-1 rounded"
                  style={{ background: "#FEF3C7", color: "#78350F", border: "1px solid #CA8A04", fontWeight: 700 }}
                  data-testid={`match-pending-${match.user_id}`}>
              · Awaiting candidate
            </span>
          ) : intro?.status === "declined" ? (
            <span className="font-mono-rm text-[10px] uppercase tracking-widest px-2 py-1 rounded"
                  style={{ background: "#FEE2E2", color: "#991B1B", border: "1px solid #B91C1C", fontWeight: 700 }}
                  data-testid={`match-declined-${match.user_id}`}>
              · Declined
            </span>
          ) : (
            <button
              type="button"
              onClick={requestIntro}
              disabled={busy}
              className="rm-btn text-[11px] px-3 py-2"
              style={{
                background: NAVY, color: "white",
                border: `2px solid ${GREEN_DEEP}`,
                boxShadow: `2px 2px 0 0 ${GOLD}`, fontWeight: 700,
              }}
              data-testid={`match-request-intro-${match.user_id}`}
            >
              {busy ? <Loader size={11} className="animate-spin" /> : <Send size={11} />}
              {busy ? "Sending…" : "Request intro"}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function CreateRequisitionModal({ onClose, onCreated }) {
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [seniority, setSeniority] = useState("");
  const [skillsText, setSkillsText] = useState("");
  const [niceText, setNiceText] = useState("");
  const [salaryBand, setSalaryBand] = useState("");
  const [nSlots, setNSlots] = useState(5);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const skills = useMemo(() => skillsText.split(",").map((s) => s.trim()).filter(Boolean), [skillsText]);
  const niceSkills = useMemo(() => niceText.split(",").map((s) => s.trim()).filter(Boolean), [niceText]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!role) { setError("Role is required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/recruiter/requisitions", {
        role,
        company: company || null,
        location: location || null,
        seniority: seniority || null,
        required_skills: skills,
        nice_to_have: niceSkills,
        salary_band: salaryBand || null,
        n_slots: nSlots,
        notes: notes || null,
      });
      onCreated?.(data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't create requisition.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(10, 37, 64, 0.6)" }}
         onClick={onClose}
         data-testid="matchmaker-create-modal">
      <div className="rm-card-static p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           style={{ background: "white", borderColor: NAVY, boxShadow: `6px 6px 0 0 ${GOLD}` }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                 style={{ color: GREEN, fontWeight: 700 }}>
              · Resmo Pro · New requisition · 50 credits
            </div>
            <h2 className="font-display uppercase text-2xl leading-tight"
                style={{ color: NAVY, letterSpacing: "-0.02em" }}>
              Describe the role.
            </h2>
            <p className="text-[12px] mt-1" style={{ color: "#475569" }}>
              Max 5 requisition runs per recruiter per day. The match engine
              scores every opted-in candidate.
            </p>
          </div>
          <button onClick={onClose}
                  className="rm-btn rm-btn-ghost text-xs px-2 py-2"
                  data-testid="matchmaker-create-close">
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="matchmaker-create-form">
          <div>
            <label className="rm-label text-[11px]">Role / Title *</label>
            <input value={role} onChange={(e) => setRole(e.target.value)}
                   className="rm-input w-full"
                   placeholder="Senior Backend Engineer · Remote"
                   data-testid="matchmaker-create-role" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="rm-label text-[11px]">Company (optional)</label>
              <input value={company} onChange={(e) => setCompany(e.target.value)}
                     className="rm-input w-full"
                     placeholder="Acme Talent client"
                     data-testid="matchmaker-create-company" />
            </div>
            <div>
              <label className="rm-label text-[11px]">Location (optional)</label>
              <input value={location} onChange={(e) => setLocation(e.target.value)}
                     className="rm-input w-full"
                     placeholder="London, Remote-EU, …"
                     data-testid="matchmaker-create-location" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="rm-label text-[11px]">Seniority (optional)</label>
              <input value={seniority} onChange={(e) => setSeniority(e.target.value)}
                     className="rm-input w-full"
                     placeholder="Senior · Lead · Staff"
                     data-testid="matchmaker-create-seniority" />
            </div>
            <div>
              <label className="rm-label text-[11px]">Salary band (optional)</label>
              <input value={salaryBand} onChange={(e) => setSalaryBand(e.target.value)}
                     className="rm-input w-full"
                     placeholder="£90-120k + equity"
                     data-testid="matchmaker-create-salary" />
            </div>
          </div>
          <div>
            <label className="rm-label text-[11px]">Required skills (comma separated)</label>
            <input value={skillsText} onChange={(e) => setSkillsText(e.target.value)}
                   className="rm-input w-full"
                   placeholder="Python, FastAPI, MongoDB, AWS"
                   data-testid="matchmaker-create-skills" />
            {skills.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {skills.map((s) => (
                  <span key={s} className="font-mono-rm text-[9px] uppercase tracking-widest px-2 py-0.5 rounded"
                        style={{ background: "#ECFDF5", color: GREEN_DEEP, border: `1px solid ${GREEN}`, fontWeight: 700 }}>
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="rm-label text-[11px]">Nice-to-have skills (optional, comma separated)</label>
            <input value={niceText} onChange={(e) => setNiceText(e.target.value)}
                   className="rm-input w-full"
                   placeholder="GraphQL, Terraform"
                   data-testid="matchmaker-create-nice" />
          </div>
          <div>
            <label className="rm-label text-[11px]">How many candidates to surface? ({nSlots})</label>
            <input type="range" min={1} max={10} value={nSlots}
                   onChange={(e) => setNSlots(parseInt(e.target.value))}
                   className="w-full"
                   data-testid="matchmaker-create-nslots" />
          </div>
          <div>
            <label className="rm-label text-[11px]">Internal notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2} className="rm-input w-full"
                      placeholder="Client constraints, must-haves, etc."
                      data-testid="matchmaker-create-notes" />
          </div>

          {error && (
            <div className="rm-card-static p-2 text-xs font-mono-rm"
                 style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
                 data-testid="matchmaker-create-error">
              · {error}
            </div>
          )}
          <div className="flex justify-end gap-2 flex-wrap pt-1">
            <button type="button" onClick={onClose}
                    className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                    data-testid="matchmaker-create-cancel">
              Cancel
            </button>
            <button type="submit" disabled={busy}
                    className="rm-btn text-sm px-4 py-2.5"
                    style={{
                      background: GREEN, color: "white",
                      border: `2px solid ${GREEN_DEEP}`,
                      boxShadow: `3px 3px 0 0 ${NAVY}`, fontWeight: 700,
                    }}
                    data-testid="matchmaker-create-submit">
              {busy ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {busy ? "Matching…" : "Run match · 50 cr"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diffMs = Date.now() - t;
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "";
  }
}
